import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell, Search } from "lucide-react";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { useCommandPalette } from "@/lib/command-palette";
import { NotificationsDrawer } from "./NotificationsDrawer";

/**
 * Pencil component `FW5hQ` (`v2/TopBar`). Sits above the page content.
 *   - Left  : breadcrumb derived from the route segments
 *   - Right : global search · notification bell (with unread badge) · ⌘K hint
 */

const ROUTE_LABELS: Record<string, string> = {
  "": "Dashboard",
  bookings: "Bookings",
  calendar: "Calendar",
  clients: "Clients",
  inventory: "Inventory",
  services: "Services",
  transactions: "Transactions",
  insights: "Insights",
  settings: "Settings",
};

export function TopBar() {
  const { pathname } = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const palette = useCommandPalette();
  const [bellOpen, setBellOpen] = useState(false);

  const segments = pathname.split("/").filter(Boolean);
  const crumbs: { label: string; to: string }[] = [{ label: "Home", to: "/" }];
  segments.forEach((seg, i) => {
    const to = "/" + segments.slice(0, i + 1).join("/");
    crumbs.push({ label: ROUTE_LABELS[seg] ?? capitalize(seg), to });
  });

  // Unread count powers the red badge on the bell. Refetched in the background
  // so the operator sees new notifications without opening the drawer.
  const feed = useQuery({
    queryKey: ["notifications.unreadCount"],
    enabled: !!accessToken,
    queryFn: async () => unwrap(await api.GET("/api/v1/notifications", {})),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const unread = feed.data?.unreadCount ?? 0;

  return (
    <>
      <header className="sticky top-0 z-10 bg-background border-b border-border">
        <div className="h-14 flex items-center justify-between px-6">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground">
            {crumbs.map((c, i) => (
              <span key={c.to} className="flex items-center gap-2">
                {i > 0 && <span className="text-border">/</span>}
                {i === crumbs.length - 1 ? (
                  <span className="text-foreground font-medium">{c.label}</span>
                ) : (
                  <Link to={c.to} className="hover:text-foreground">
                    {c.label}
                  </Link>
                )}
              </span>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={palette.open}
              className="relative hidden md:flex items-center pl-8 pr-12 h-9 w-72 rounded-md bg-muted/40 hover:bg-muted/60 text-sm text-muted-foreground transition-colors"
              aria-label="Open command palette"
            >
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4" />
              <span className="text-left flex-1 truncate">Search anything…</span>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] border border-border rounded px-1 py-0.5 font-mono bg-background">
                ⌘K
              </span>
            </button>

            <button
              type="button"
              onClick={() => setBellOpen(true)}
              className="relative h-9 w-9 rounded-md hover:bg-muted/60 inline-flex items-center justify-center"
              aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ""}`}
            >
              <Bell className="h-4 w-4" />
              {unread > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold inline-flex items-center justify-center tabular-nums leading-none">
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>

      <NotificationsDrawer open={bellOpen} onClose={() => setBellOpen(false)} />
    </>
  );
}

function capitalize(s: string) {
  if (!s) return s;
  // Truncate UUID-ish strings to first 8 chars so the breadcrumb isn't a wall.
  if (/^[0-9a-f]{8}-[0-9a-f]{4}/.test(s)) return `#${s.slice(0, 8)}`;
  return s.charAt(0).toUpperCase() + s.slice(1);
}
